/* eslint-disable no-use-before-define */
/* eslint-disable react/prop-types */
import React from 'react';
import type { FC, ReactNode } from 'react';
import { useLocation, matchPath, Link as RouterLink } from 'react-router-dom';
import { Box, Drawer, Hidden, List, ListSubheader, makeStyles } from '@material-ui/core';
import { Briefcase, Clock, Edit, Phone } from 'react-feather';

import NavItem from './NavItem';

import Logo from 'src/ThemeComponents/components/Logo';

interface NavBarProps {
  onMobileClose: (close) => void;
  openMobile: boolean;
}

interface Item {
  href?: string;
  icon?: ReactNode;
  info?: ReactNode;
  items?: Item[];
  title: string;
}

interface Section {
  items: Item[];
  subheader: string;
}

interface NavItems {
  items: Item[];
  pathname: string;
  depth?: number;
}

interface ChildRoutes {
  acc: any[];
  pathname: string;
  item: Item;
  depth: number;
}

const sections: Section[] = [
  {
    subheader: 'Overview',
    items: [{
      title: 'Orders',
      icon: Briefcase,
      href: '/orders'
    }, {
      title: 'Hours of Operations',
      icon: Clock,
      href: '/hours-of-operations'
    }, {
      title: 'Contact Us',
      icon: Phone,
      href: '/contact-us'
    }, {
      title: 'Change Password',
      icon: Edit,
      href: '/change-password'
    }]
  }
];

const renderNavItems = ({ items, pathname, depth = 0 }: NavItems) => {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

const reduceChildRoutes = ({ acc, pathname, item, depth }: ChildRoutes) => {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  avatar: {
    cursor: 'pointer',
    width: 64,
    height: 64
  }
}));

const NavBar: FC<NavBarProps> = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();

  const content = (
    <Box height='100%' display='flex' flexDirection='column'>
      <Hidden lgUp>
        <Box display='flex' justifyContent='center'>
          <RouterLink to='/'>
            <Logo height='100px' link='/static/Logo.png' />
          </RouterLink>
        </Box>
      </Hidden>
      <Box p={2}>
        {sections.map((section) => (
          <List
            key={section.subheader}
            subheader={(
              <ListSubheader disableGutters disableSticky>
                {section.subheader}
              </ListSubheader>
            )}
          >
            {renderNavItems({
              items: section.items,
              pathname: location.pathname
            })}
          </List>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor='left'
          classes={{ paper: classes.mobileDrawer }}
          onClose={() => onMobileClose(false)}
          open={openMobile}
          variant='temporary'
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor='left'
          classes={{ paper: classes.desktopDrawer }}
          open
          variant='persistent'
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

export default NavBar;
